import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, LogOut, Settings, Menu, HelpCircle } from 'lucide-react';
import { Group, ActionIcon, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useUser } from '../contexts/UserContext';
import { useDialog } from '../contexts/DialogContext';
import { useNavigation } from '../contexts/NavigationContext';
import { NotificationBell } from './NotificationBell';
import PWAInstallButton from './PWAInstallButton';
import { useNotifications } from '../hooks/useNotifications';
import './AppHeader.css';

interface AppHeaderProps {
  onShowNotifications?: () => void;
  onShowFeedback?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ onShowNotifications, onShowFeedback }) => {
  const { currentUser, logout } = useUser();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { toggleNavbar } = useNavigation();
  const { unreadCount } = useNotifications(currentUser?.email || '');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Use Mantine's media query hook to match AppShell breakpoint exactly
  const isMobile = useMediaQuery('(max-width: 991px)'); // md breakpoint - 1px less than 992px

  const handleLogout = async () => {
    confirm(
      'Confirm Logout',
      'Are you sure you want to log out?',
      async () => {
        await logout();
      }
    );
  };

  // Handle clicking outside the user dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserDropdown && !(event.target as Element).closest('.user-display')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  if (!currentUser) {
    return null;
  }

  return (
    <div className="app-header-content">
      <Group justify="space-between" w="100%" className="app-header-main-row">
        <Group gap="md" className="app-header-brand-group">
          {isMobile && (
            <ActionIcon
              onClick={toggleNavbar}
              variant="subtle"
              size="lg"
              aria-label="Toggle navigation"
            >
              <Menu size={18} />
            </ActionIcon>
          )}
          
          <Group gap="sm">
            <Music size={20} style={{ color: '#3b82f6' }} />
            <div>
              <Text size="lg" fw={600} c="var(--app-text-primary)" className="app-header-title">
                SymphonyTodo
              </Text>
              <Text size="xs" c="var(--app-text-secondary)" style={{ lineHeight: 1 }} className="app-header-tagline">
                Collaborative productivity
              </Text>
            </div>
          </Group>
        </Group>

        <Group gap="sm" className="app-header-actions-group">
          <PWAInstallButton variant="auto" />
          
          <div className="user-display">
            <div className="user-info">
              <span className="user-name">{currentUser.name}</span>
              <span className="user-email">{currentUser.email}</span>
            </div>
            
            <NotificationBell
              unreadCount={unreadCount}
              onClick={onShowNotifications || (() => {})}
            />
            
            <div 
              className="user-avatar clickable"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
              title={`${currentUser.name} (${currentUser.email})`}
            >
              <span className="user-initials">
                {currentUser.name
                  .split(' ')
                  .map(word => word.charAt(0))
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            </div>
            
            {showUserDropdown && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <span className="dropdown-user-name">{currentUser.name}</span>
                  <span className="dropdown-user-email">{currentUser.email}</span>
                </div>
                <div className="user-dropdown-actions">
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowUserDropdown(false);
                    }}
                    className="dropdown-action-btn"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      onShowFeedback?.();
                      setShowUserDropdown(false);
                    }}
                    className="dropdown-action-btn"
                  >
                    <HelpCircle size={16} />
                    Help & Feedback
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setShowUserDropdown(false);
                    }}
                    className="dropdown-action-btn logout"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              </div>
            )}
            
            <button
              onClick={() => navigate('/settings')}
              className="settings-btn"
              title="Settings"
              aria-label="Settings"
            >
              <Settings size={16} />
            </button>
            
            <button
              onClick={handleLogout}
              className="logout-btn"
              title="Log out"
              aria-label="Log out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </Group>
      </Group>
    </div>
  );
};
